import { Button, Table } from 'antd';
import { useContext, useEffect, useState } from 'react';
import APIList from '../../../../http/ApiList';
import { AppContext } from '../../../../contexts/AppContext';
import { ArrowUpOutlined, ArrowRightOutlined, ArrowDownOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import { ChartEmpty, ChartTitle, Loading } from '@meteor/frontend-core';
import { cloneDeep, isFunction, isObject, isString } from 'lodash';
import dayjs from 'dayjs';
import ErrorBoundary from '../../../common/error-boundary';

const BasicTable: React.FC<any> = (props) => {
  const { authInfo, setGlobalEvent, globalEvent } = useContext(AppContext);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableData, setTableData] = useState<any>([]);
  const [tableColumn, setTableColumn] = useState<any>([]);
  const { property } = props;

  const arrowMapping = {
    up: <ArrowUpOutlined />,
    right: <ArrowRightOutlined />,
    down: <ArrowDownOutlined />,
    left: <ArrowLeftOutlined />,
  };

  const renderByFormat = (renderFormat: string, text: string, record: any, index: number) => {
    switch (renderFormat) {
      case 'Date':
        return dayjs(new Date(text)).format('YYYY-MM-DD');
      case 'Currency':
        return <div style={{ textAlign: 'right' }}>{Number(text).toLocaleString()}</div>;
      case 'Percent':
        return <div style={{ textAlign: 'right' }}>{(Number(text) * 100).toFixed(2) + '%'}</div>;
      case 'Arrow':
        return <div style={{ color: record.arrow_color }}>{arrowMapping[record.arrow_direction]}</div>;
      default:
        break;
    }
  };

  const getTableData = (event: any) => {
    setTableLoading(true);
    APIList.getCustomizeLayoutData()
      .post({ email: authInfo?.user?.attributes?.email || '', property, event })
      .then((res: any) => {
        setTableLoading(false);
        if (res && res.data) {
          setTableData(res.data);
        }
      });
  };

  useEffect(() => {
    if (!globalEvent) {
      return;
    }
    if (
      (property.dataSource &&
        property.dataSource.triggerEvent &&
        property.dataSource.triggerEvent.find((triggerEvent) => {
          return triggerEvent.event === globalEvent.eventType;
        })) ||
      globalEvent.eventType === GLOBAL_EVENT.PAGE_INIT ||
      globalEvent.eventType === GLOBAL_EVENT.SHOW_CHAT_CONTENT
    ) {
      getTableData(globalEvent);
    }
  }, [globalEvent]);

  useEffect(() => {
    if (!property) {
      return;
    }
    const tempProperty = cloneDeep(property);
    let tempColumns: any = [];
    if (property.tableColumns && property.tableColumns.columns && property.tableColumns.columns.length > 0) {
      if (tempProperty.tableColumns.tableType === 'Complex Table') {
        const reviver = (key, value) => {
          const arrowFunctionRegex = /^\s*\(?\s*([^)]*)\s*\)?\s*=>\s*{[^]*}$/;
          if (typeof value === 'string' && (value.startsWith('function') || arrowFunctionRegex.test(value))) {
            return new Function('return ' + value)();
          }
          return value;
        };
        try {
          tempColumns = JSON.parse(tempProperty.tableColumns.columns, reviver);
        } catch (error) {
          console.log(tempProperty.tableColumns.columns);
          console.error(error);
        }
      } else {
        tempColumns = tempProperty.tableColumns.columns;
      }
    } else if (property.tableColumns && property.tableColumns.length > 0) {
      tempColumns = tempProperty.tableColumns;
    }

    if (tempColumns.length > 0) {
      const columns = tempColumns.map((column) => {
        let renderFunc = null;

        if (column.render && isString(column.render) && column.render.length > 0) {
          try {
            if (!column.render.startsWith('(text, record, index)')) {
              renderFunc = new Function(`return (text, record, index) => { ${column.render} }`).bind(this)();
            } else {
              renderFunc = new Function(`return ${column.render}`).bind(this)();
            }
          } catch (error) {
            console.error(error);
            renderFunc = null;
          }
        } else if (column.render && isFunction(column.render)) {
          renderFunc = column.render;
        }

        column.render = (text: string, record: any, index: number) => {
          let renderResult = null;
          if (!column.renderFormat || column.renderFormat === 'Customize') {
            if (renderFunc) {
              try {
                renderResult = renderFunc(text, record, index);
              } catch (error) {
                console.error(error);
              }
            }
            renderResult = renderResult && !isObject(renderResult) ? renderResult : column.dataIndex ? record[column.dataIndex] : '';
          } else {
            renderResult = renderByFormat(column.renderFormat, text, record, index);
            renderResult = renderResult ? renderResult : column.dataIndex ? record[column.dataIndex] : '';
          }

          return (
            <div
              onClick={() => {
                if (record) {
                  const eventInfo = {
                    eventType: GLOBAL_EVENT.TABLE_ITEM_CLICK,
                    data: record,
                  };
                  setGlobalEvent(eventInfo);
                }
              }}
            >
              {renderResult}
            </div>
          );
        };
        return column;
      });
      setTableColumn(columns);
      getTableData(globalEvent);
    }
  }, [property]);

  const hasTitle = property && property.title && property.title.length > 0;

  const hasAddBtn = property && property.addBtnName && property.addBtnName.length > 0;

  return (
    <ErrorBoundary>
      <div className="resizable-table" style={{ padding: '10px 12px', background: '#fff', height: '100%' }}>
        {hasTitle ? (
          <ChartTitle
            title={{
              value: property.title,
            }}
          />
        ) : (
          <></>
        )}
        {hasAddBtn ? (
          <Button type="primary" style={{ position: 'absolute', right: 12, zIndex: 999, top: 12 }}>
            {property.addBtnName}
          </Button>
        ) : (
          <></>
        )}

        {tableData.length > 0 ? (
          <Table
            style={{ background: '#FFF', border: '1px solid #eee', height: hasTitle ? 'calc(100% - 44px)' : '100%' }}
            scroll={{ y: 'calc(100% - ' + (property.tableHeaderLevelCount * 30 + 30) + 'px)' }}
            pagination={false}
            columns={tableColumn}
            dataSource={tableData}
            loading={tableLoading}
            className="scroll-table"
            size={'small'}
            expandable={{
              defaultExpandAllRows: true,
            }}
            bordered
          />
        ) : (
          <>{!tableLoading ? <ChartEmpty /> : <Loading fixed={false} />}</>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default BasicTable;
